import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, KeyRound, Mail, ChevronRight, ExternalLink } from 'lucide-react'
import { useTelegram } from '../../hooks/useTelegram'
import { FatSecretAttribution } from '../FatSecretAttribution'

const F = 'Roboto, sans-serif'

const FATSECRET_RESET_URL = 'https://foods.fatsecret.com/Default.aspx?pa=p'
const SUPPORT_URL = 'https://t.me/sup_TFA'

interface Props {
  open: boolean
  onClose: () => void
  onConnect: () => void
  isPending?: boolean
  mode?: 'connect' | 'reconnect'
}

type Branch = 'password' | 'sso' | null

export function FatSecretConnectSheet({ open, onClose, onConnect, isPending = false, mode = 'connect' }: Props) {
  const { impactHaptic, notificationHaptic, openLink } = useTelegram()
  const [branch, setBranch] = useState<Branch>(null)

  useEffect(() => {
    if (!open) setBranch(null)
  }, [open])

  const isReconnect = mode === 'reconnect'
  const title = isReconnect ? 'Переподключи FatSecret' : 'Подключи FatSecret'
  const primaryCta = isReconnect ? 'Переподключить' : 'Подключить'

  const selectBranch = (b: Branch) => {
    notificationHaptic('success')
    setBranch(b)
  }

  const handleConnect = () => {
    impactHaptic('medium')
    onConnect()
  }

  const handleOpenReset = () => {
    impactHaptic('medium')
    openLink(FATSECRET_RESET_URL, { external: true })
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)', zIndex: 400,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="fatsecret-connect-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(30px)',
              borderTopLeftRadius: 26, borderTopRightRadius: 26,
              maxHeight: '90vh', overflowY: 'auto',
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            {/* Handle */}
            <SheetGrip onClose={onClose} />

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12,
                  background: 'linear-gradient(135deg,#66BB6A,#2E7D32)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 14, fontWeight: 800, letterSpacing: 0.5,
                }}>FS</div>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C2D41', margin: 0 }}>{title}</h2>
                  <p style={{ fontSize: 12, color: '#9AA5B4', margin: 0 }}>
                    Выбери, как ты входишь в FatSecret — мы проведём до конца
                  </p>
                </div>
              </div>
              <button
                data-testid="fatsecret-sheet-close"
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%',
                  width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>
            </div>

            {/* Branch cards */}
            <div style={{ padding: '12px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <BranchCard
                testid="fatsecret-branch-password"
                icon={<KeyRound size={18} color="#2E7D32" />}
                title="По email и паролю"
                subtitle="Обычный вход — у тебя есть пароль FatSecret"
                selected={branch === 'password'}
                onClick={() => selectBranch('password')}
              />
              {branch === 'password' && (
                <button
                  data-testid="fatsecret-branch-password-cta"
                  onClick={handleConnect}
                  disabled={isPending}
                  style={primaryButtonStyle(isPending)}
                >
                  {isPending ? '...' : primaryCta}
                </button>
              )}

              <BranchCard
                testid="fatsecret-branch-sso"
                icon={<Mail size={18} color="#2E7D32" />}
                title="Через Google или Apple"
                subtitle="На странице FatSecret в браузере нет входа через Google и Apple — нужен пароль"
                selected={branch === 'sso'}
                onClick={() => selectBranch('sso')}
              />
              {branch === 'sso' && (
                <div
                  data-testid="fatsecret-sso-guide"
                  style={{
                    borderRadius: 14, padding: '14px 14px 12px',
                    background: 'rgba(76,175,80,0.06)',
                    border: '1px solid rgba(76,175,80,0.18)',
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}
                >
                  <p style={{ fontSize: 13, color: '#1C2D41', margin: 0, lineHeight: 1.5 }}>
                    FatSecret в браузере принимает только email и пароль. Установи его один раз — дальше всё подключится.
                  </p>
                  <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <li style={stepStyle}>
                      Узнай email, к которому привязан твой FatSecret-аккаунт. Если регистрировался через <b>Apple Sign-In</b> — он может выглядеть как <code style={codeStyle}>xxx@privaterelay.appleid.com</code>. Найти можно: iPhone → Настройки → Apple ID → Вход с Apple ID → FatSecret.
                    </li>
                    <li style={stepStyle}>
                      Открой восстановление пароля FatSecret и введи этот email.
                    </li>
                    <li style={stepStyle}>
                      Открой ссылку из письма <b>в браузере</b> и задай новый пароль.
                    </li>
                    <li style={stepStyle}>
                      Войди на <b>fatsecret.com</b> в браузере с этим email и новым паролем.
                    </li>
                    <li style={stepStyle}>
                      Вернись сюда и нажми «Я задал пароль — {primaryCta}».
                    </li>
                  </ol>

                  <div style={warnBoxStyle}>
                    ⚠️ Ссылка из письма часто открывается в приложении FatSecret с надписью
                    «Your password reset link is invalid» — это и есть ловушка. Зажми ссылку
                    в письме и выбери «Открыть в Safari» (или открой письмо на компьютере),
                    чтобы попасть на страницу сброса в браузере, а не в приложении. Ссылка
                    действует недолго — переходи по ней сразу.
                  </div>

                  <button
                    data-testid="fatsecret-open-reset"
                    onClick={handleOpenReset}
                    style={secondaryButtonStyle}
                  >
                    <ExternalLink size={15} color="#2E7D32" />
                    <span>Открыть восстановление пароля FatSecret</span>
                  </button>

                  <p style={{ fontSize: 11, color: '#9AA5B4', margin: 0, lineHeight: 1.45 }}>
                    Сначала войди в FatSecret в браузере с новым паролем — потом возвращайся сюда.
                  </p>

                  <button
                    data-testid="fatsecret-branch-sso-cta"
                    onClick={handleConnect}
                    disabled={isPending}
                    style={primaryButtonStyle(isPending)}
                  >
                    {isPending ? '...' : `Я задал пароль — ${primaryCta}`}
                  </button>
                </div>
              )}
            </div>

            {/* Support link */}
            <div style={{ padding: '14px 20px 4px' }}>
              <button
                data-testid="fatsecret-sheet-support"
                onClick={() => openLink(SUPPORT_URL)}
                style={{
                  width: '100%', height: 44, borderRadius: 12, border: 'none',
                  background: 'transparent', color: '#6B7B8D',
                  fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: F,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                Не получилось? Напиши нам
                <ChevronRight size={14} color="#A0AEC0" />
              </button>
            </div>

            {/* Атрибуция FatSecret — обязательна по их Attribution Policy. */}
            <div style={{ padding: '2px 20px 12px', display: 'flex', justifyContent: 'center' }}>
              <FatSecretAttribution variant="block" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const stepStyle: React.CSSProperties = {
  fontSize: 13, color: '#1C2D41', lineHeight: 1.5,
}

const codeStyle: React.CSSProperties = {
  fontSize: 12, padding: '1px 5px', borderRadius: 4,
  background: 'rgba(26,58,92,0.07)', color: '#1C2D41',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
}

const warnBoxStyle: React.CSSProperties = {
  fontSize: 12, lineHeight: 1.5, color: '#92400E',
  background: 'rgba(245,158,11,0.10)',
  border: '1px solid rgba(245,158,11,0.35)',
  borderRadius: 12, padding: '10px 12px',
}

function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%', height: 48, borderRadius: 14, border: 'none',
    background: 'linear-gradient(135deg,#66BB6A,#2E7D32)',
    color: 'white', fontSize: 14, fontWeight: 700,
    cursor: disabled ? 'wait' : 'pointer',
    boxShadow: '0 4px 16px rgba(76,175,80,0.32)',
    opacity: disabled ? 0.7 : 1,
    fontFamily: F,
  }
}

const secondaryButtonStyle: React.CSSProperties = {
  width: '100%', height: 44, borderRadius: 12,
  background: 'rgba(76,175,80,0.10)',
  border: '1px solid rgba(76,175,80,0.30)',
  color: '#2E7D32', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: F,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
}

interface BranchCardProps {
  testid: string
  icon: React.ReactNode
  title: string
  subtitle: string
  selected: boolean
  onClick: () => void
}

function BranchCard({ testid, icon, title, subtitle, selected, onClick }: BranchCardProps) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        textAlign: 'left', cursor: 'pointer', fontFamily: F,
        borderRadius: 14, padding: '12px 14px',
        background: selected ? 'rgba(76,175,80,0.08)' : 'rgba(255,255,255,0.85)',
        border: `1.5px solid ${selected ? 'rgba(76,175,80,0.55)' : 'rgba(26,58,92,0.10)'}`,
        display: 'flex', alignItems: 'center', gap: 12,
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
        background: 'rgba(76,175,80,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1C2D41', marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#6B7B8D', lineHeight: 1.4 }}>{subtitle}</div>
      </div>
      <div style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
        border: `2px solid ${selected ? '#2E7D32' : 'rgba(26,58,92,0.20)'}`,
        background: selected ? '#2E7D32' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'white' }} />}
      </div>
    </button>
  )
}
